import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SkuDTO } from "@mydailydirect/api-client";
import {
  formatPaise,
  Button,
  SearchBar,
  Dialog,
  DialogHeader,
  DialogFooter,
  useToast,
} from "@mydailydirect/ui";
import {
  Sparkles,
  Plus,
  Minus,
  Trash2,
  Package,
  PlusCircle,
  HelpCircle,
  Check,
} from "lucide-react";

export interface OnboardingFocusPackageModalProps {
  open: boolean;
  onClose: () => void; // Triggered when user skips or closes
  onFinished?: () => void; // Triggered when user saves or completes
  onComplete?: () => void; // Alias for onFinished
  retailerId: number;
  retailerName: string;
  client: any;
  availableSkus?: SkuDTO[];
}

interface SelectedFocusItem {
  sku: SkuDTO;
  qty: number;
}

interface MissingItemSuggestion {
  productName: string;
  category: string;
  expectedQty: number;
  targetPrice?: string;
  comments?: string;
}

export function OnboardingFocusPackageModal({
  open,
  onClose,
  onFinished,
  onComplete,
  retailerId,
  retailerName,
  client,
  availableSkus = [],
}: OnboardingFocusPackageModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<SelectedFocusItem[]>([]);
  const [isSuggestFormOpen, setIsSuggestFormOpen] = useState(false);
  const [missingItem, setMissingItem] = useState<MissingItemSuggestion>({
    productName: "",
    category: "Edible Oil",
    expectedQty: 5,
    targetPrice: "",
    comments: "",
  });
  const [pendingSuggestions, setPendingSuggestions] = useState<MissingItemSuggestion[]>([]);

  // Fetch full active SKU catalog
  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    enabled: open,
  });

  const allSkus: SkuDTO[] = availableSkus.length > 0
    ? availableSkus.filter((s: SkuDTO) => s.active)
    : (skusQuery.data ?? []).filter((s: SkuDTO) => s.active);

  // Filter available SKUs
  const selectedSkuIds = new Set(selectedItems.map((i) => i.sku.id));
  const filteredCandidates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allSkus
      .filter((s) => !selectedSkuIds.has(s.id ?? 0))
      .filter((s) => {
        if (!q) return true;
        return (
          s.name.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q) ||
          (s.category ?? "").toLowerCase().includes(q) ||
          (s.brand ?? "").toLowerCase().includes(q)
        );
      });
  }, [allSkus, selectedSkuIds, searchQuery]);

  // Handle adding an item from catalog
  const handleAddItem = (sku: SkuDTO) => {
    let defaultQty = 2;
    const cat = (sku.category ?? "").toLowerCase();
    if (cat.includes("oil")) defaultQty = 5;
    else if (cat.includes("sugar")) defaultQty = 2;
    else if (cat.includes("atta") || cat.includes("flour")) defaultQty = 4;

    setSelectedItems((prev) => [...prev, { sku, qty: defaultQty }]);
    setSearchQuery("");
  };

  const handleUpdateQty = (skuId: number, delta: number) => {
    setSelectedItems((prev) =>
      prev.map((i) => {
        if (i.sku.id === skuId) {
          return { ...i, qty: Math.max(1, i.qty + delta) };
        }
        return i;
      })
    );
  };

  const handleRemoveItem = (skuId: number) => {
    setSelectedItems((prev) => prev.filter((i) => i.sku.id !== skuId));
  };

  // Handle adding a missing suggestion
  const handleAddSuggestion = () => {
    if (!missingItem.productName.trim()) {
      toast({
        title: "Product Name Required",
        description: "Please enter the name of the missing product.",
        variant: "error",
      });
      return;
    }

    setPendingSuggestions((prev) => [...prev, { ...missingItem }]);
    setMissingItem({
      productName: "",
      category: "Edible Oil",
      expectedQty: 5,
      targetPrice: "",
      comments: "",
    });
    setIsSuggestFormOpen(false);
    toast({
      title: "Product Suggestion Added! 💡",
      description: `Added "${missingItem.productName}" to the buyer's requested items list.`,
      variant: "success",
    });
  };

  // Mutation to save Focus Package and suggestions
  const saveFocusPackageMutation = useMutation({
    mutationFn: async () => {
      // 1. Save focus package items if any selected
      if (selectedItems.length > 0) {
        await client.focusPackages.save(retailerId, {
          name: `${retailerName} Focus Basket`,
          description: "Regular staples packet captured during store onboarding",
          items: selectedItems.map((i) => ({
            skuId: i.sku.id!,
            defaultQty: i.qty,
          })),
        });
      }

      // 2. Submit missing product suggestions if any
      for (const s of pendingSuggestions) {
        const targetPaise = s.targetPrice ? Math.round(parseFloat(s.targetPrice) * 100) : null;
        await client.focusPackages.submitRequest({
          retailerId,
          productName: s.productName.trim(),
          category: s.category,
          expectedQty: s.expectedQty,
          targetPricePaise: targetPaise,
          comments: s.comments ? s.comments.trim() : null,
        }).catch((err: any) => console.warn("Failed to record product suggestion:", err));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["focusPackage", retailerId] });
      toast({
        title: "Regular Focus Basket Configured! 🎯",
        description: `Saved ${selectedItems.length} regular staples and ${pendingSuggestions.length} requested items.`,
        variant: "success",
      });
      if (onFinished) onFinished();
      else if (onComplete) onComplete();
    },
    onError: (err) => {
      toast({
        title: "Could not save focus basket",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    },
  });

  const estimatedTotalPaise = selectedItems.reduce(
    (sum, i) => sum + (i.sku.pricePaise || 0) * i.qty,
    0
  );

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader>
        <div className="flex items-center justify-between gap-3 border-b pb-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xs font-extrabold uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                  Step 2 of 2 · Optional
                </span>
                <span className="text-xs text-muted-foreground font-medium">Quick Basket Setup</span>
              </div>
              <h3 className="text-base font-extrabold text-foreground mt-0.5">
                What does {retailerName} regularly buy?
              </h3>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-xs font-bold text-muted-foreground hover:text-foreground"
          >
            Skip for Now &rarr;
          </Button>
        </div>
      </DialogHeader>

      <div className="space-y-4 py-3 max-h-[68vh] overflow-y-auto pr-1">
        <p className="text-xs text-muted-foreground">
          Select recurring staples (oils, sugar, rice, flour) so the RM or buyer doesn't have to search through 100 products during orders.
        </p>

        {/* Catalog Search Bar */}
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search products by brand or name (e.g. Sunpure, Sugar, Atta)..."
          className="w-full"
        />

        {/* Search Results Dropdown / Candidate Rail */}
        {searchQuery.trim().length > 0 && (
          <div className="border rounded-xl p-2 bg-muted/20 max-h-48 overflow-y-auto space-y-1">
            {filteredCandidates.length === 0 ? (
              <div className="text-center py-4 text-xs text-muted-foreground">
                <p>No products matching "{searchQuery}".</p>
                <button
                  type="button"
                  onClick={() => {
                    setMissingItem((prev) => ({ ...prev, productName: searchQuery }));
                    setIsSuggestFormOpen(true);
                  }}
                  className="mt-2 text-indigo-600 dark:text-indigo-400 font-bold hover:underline inline-flex items-center gap-1"
                >
                  <PlusCircle className="h-3.5 w-3.5" /> Suggest "{searchQuery}" as a new product
                </button>
              </div>
            ) : (
              filteredCandidates.slice(0, 15).map((sku) => (
                <div
                  key={sku.id}
                  onClick={() => handleAddItem(sku)}
                  className="p-2.5 rounded-lg border border-border bg-background hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30 transition-all flex items-center justify-between gap-3 cursor-pointer group"
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      {sku.brand && (
                        <span className="text-[10px] font-extrabold text-indigo-600 uppercase">
                          {sku.brand}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">{sku.category}</span>
                    </div>
                    <p className="text-xs font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                      {sku.name}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs font-mono font-extrabold text-foreground">
                      {formatPaise(sku.pricePaise)}
                    </span>
                    <span className="h-7 px-2 rounded-md bg-indigo-600 text-white font-bold text-xs flex items-center justify-center gap-1">
                      <Plus className="h-3 w-3" /> Add
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Selected Regular Items Basket */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Package className="h-4 w-4 text-indigo-600" /> Selected Regular Items ({selectedItems.length})
            </span>
            {selectedItems.length > 0 && (
              <span className="text-xs font-black text-indigo-600 font-mono">
                Est. Total: {formatPaise(estimatedTotalPaise)}
              </span>
            )}
          </div>

          {selectedItems.length === 0 ? (
            <div className="p-4 rounded-xl border-2 border-dashed border-border text-center space-y-1.5 bg-muted/10">
              <p className="text-xs font-medium text-muted-foreground">
                No items added yet. Search products above or suggest a missing item below.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {selectedItems.map(({ sku, qty }) => (
                <div
                  key={sku.id}
                  className="p-2.5 rounded-xl border border-indigo-200/60 dark:border-indigo-900/60 bg-background flex items-center justify-between gap-3 shadow-2xs"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-foreground truncate">{sku.name}</p>
                    <p className="text-2xs text-muted-foreground">
                      Rate: {formatPaise(sku.pricePaise)} {sku.unit ? `/${sku.unit}` : ""}
                    </p>
                  </div>

                  {/* Quantity Stepper */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleUpdateQty(sku.id!, -1)}
                      className="h-6 w-6 rounded border border-border flex items-center justify-center hover:bg-muted"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-7 text-center text-xs font-black">{qty}</span>
                    <button
                      type="button"
                      onClick={() => handleUpdateQty(sku.id!, 1)}
                      className="h-6 w-6 rounded border border-border flex items-center justify-center hover:bg-muted"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Line Total & Delete */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-extrabold text-foreground w-16 text-right">
                      {formatPaise((sku.pricePaise || 0) * qty)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(sku.id!)}
                      className="text-muted-foreground hover:text-rose-600 p-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Missing Item / Suggestion Section */}
        <div className="pt-2 border-t space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <HelpCircle className="h-4 w-4 text-amber-500" />
              Item Not Found in Catalog?
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsSuggestFormOpen(!isSuggestFormOpen)}
              className="h-7 text-xs font-bold gap-1 border-amber-400/50 text-amber-800 dark:text-amber-300"
            >
              <PlusCircle className="h-3.5 w-3.5" />
              {isSuggestFormOpen ? "Close Form" : "Suggest Product"}
            </Button>
          </div>

          {/* Pending Suggestions List */}
          {pendingSuggestions.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {pendingSuggestions.map((s, idx) => (
                <div
                  key={idx}
                  className="p-2.5 rounded-lg border border-amber-300/60 bg-amber-50/50 dark:bg-amber-950/20 flex items-center justify-between text-xs"
                >
                  <div>
                    <span className="font-bold text-foreground">{s.productName}</span>
                    <span className="text-muted-foreground ml-2">({s.category} · Qty: {s.expectedQty})</span>
                    {s.targetPrice && <span className="text-emerald-600 font-bold ml-2">Target: ₹{s.targetPrice}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingSuggestions((prev) => prev.filter((_, i) => i !== idx))}
                    className="text-muted-foreground hover:text-rose-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Suggestion Input Form */}
          {isSuggestFormOpen && (
            <div className="p-3.5 rounded-xl border border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20 space-y-3 animate-in fade-in">
              <p className="text-xs text-amber-900 dark:text-amber-200 font-medium">
                Buyer wants a brand/SKU we don't carry yet? Log it here so Admin can source it.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="text-[11px] font-bold text-foreground block mb-1">
                    Requested Product / Brand *
                  </label>
                  <input
                    type="text"
                    value={missingItem.productName}
                    onChange={(e) => setMissingItem((prev) => ({ ...prev, productName: e.target.value }))}
                    placeholder="e.g. Gemini 15L Tin, Fortune Biryani Rice"
                    className="w-full text-xs h-8 px-2.5 rounded-lg border border-border bg-background"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-foreground block mb-1">
                    Category / Pack Size
                  </label>
                  <input
                    type="text"
                    value={missingItem.category}
                    onChange={(e) => setMissingItem((prev) => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g. 15L Tin, 25kg Bag, 50kg Atta"
                    className="w-full text-xs h-8 px-2.5 rounded-lg border border-border bg-background"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-foreground block mb-1">
                    Weekly / Monthly Requirement
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={missingItem.expectedQty}
                    onChange={(e) =>
                      setMissingItem((prev) => ({ ...prev, expectedQty: parseInt(e.target.value) || 1 }))
                    }
                    className="w-full text-xs h-8 px-2.5 rounded-lg border border-border bg-background"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold text-foreground block mb-1">
                    Target Expected Price (₹)
                  </label>
                  <input
                    type="number"
                    value={missingItem.targetPrice}
                    onChange={(e) => setMissingItem((prev) => ({ ...prev, targetPrice: e.target.value }))}
                    placeholder="e.g. 1650"
                    className="w-full text-xs h-8 px-2.5 rounded-lg border border-border bg-background"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-bold text-foreground block mb-1">
                  Notes / Buyer Comments
                </label>
                <input
                  type="text"
                  value={missingItem.comments}
                  onChange={(e) => setMissingItem((prev) => ({ ...prev, comments: e.target.value }))}
                  placeholder="e.g. Customer uses this for all fry items; buys on Mondays"
                  className="w-full text-xs h-8 px-2.5 rounded-lg border border-border bg-background"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSuggestFormOpen(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleAddSuggestion}
                  className="text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Add Suggestion
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DialogFooter className="gap-2 border-t pt-3 flex flex-col sm:flex-row sm:items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-xs font-medium text-muted-foreground">
          Skip for Now
        </Button>

        <Button
          variant="primary"
          size="sm"
          loading={saveFocusPackageMutation.isPending}
          disabled={selectedItems.length === 0 && pendingSuggestions.length === 0}
          onClick={() => saveFocusPackageMutation.mutate()}
          className="text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/20 h-9 px-4 gap-1.5"
        >
          <Check className="h-4 w-4" /> Save Focus Basket & Finish
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
